import Element, { ElementParams } from './element';
import Sprite, { SpriteParams } from '../sprite/sprite';


import Game from '../game';
import { QuadtreeItem } from 'quadtree-lib';
import { paths } from '../../config/paths.config';

export interface CursorParams extends ElementParams {
  useLock?: boolean;
}

export default class Cursor extends Element implements CursorParams {
  defaultSpriteParams: SpriteParams;
  useLock = false;

  bordersTouched = {
    left: false,
    right: false,
    top: false,
    bottom: false,
  };

  update(): Cursor {
    return this;
  }

  get eventListenerTarget(): HTMLElement {
    return Game.instance.containerDOM;
  }

  get hasLock(): boolean {
    return (document.pointerLockElement === this.context.canvas);
  }

  isSelected(): boolean {
    return false;
  }

  isHovered(): boolean {
    return false;
  }

  constructor(params: CursorParams) {
    if (Game.instance.cursor) return Game.instance.cursor; // singleton

    super(params);
    this.applyParams(params);

    this.defaultSpriteParams = {
      src: this.sprite.src,
      offsetCenterX: this.sprite.offsetCenterX,
      offsetCenterY: this.sprite.offsetCenterY,
    };
  }

  setDefaultSprite() {
    this.sprite.image = new Image();
    this.sprite.image.src = this.defaultSpriteParams.src;
    this.sprite.offsetCenterX = this.defaultSpriteParams.offsetCenterX;
    this.sprite.offsetCenterY = this.defaultSpriteParams.offsetCenterY;
  }

  setCrossSprite() {
    this.sprite.image = new Image();
    this.sprite.image.src = `${paths.img.cursors}/cross.gif`;
    this.sprite.offsetCenterX = 0;
    this.sprite.offsetCenterY = 0;
  }

  onMouseMove = (e: MouseEvent): void => {
    const { camera } = Game.instance?.scene;

    if (this.hasLock) {
      this.x += e.movementX / (camera?.zoomRatio ?? 1);
      this.y += e.movementY / (camera?.zoomRatio ?? 1);
    }
    else {
      const { x, y } = Game.instance.controlSchemes?.rts?.getPointerCoords(e);
      this.x = x;
      this.y = y;
    }

    if (camera) {
      let minX = camera.x - camera.width / 2;
      let maxX = camera.x + camera.width / 2;
      let minY = camera.y - camera.height / 2;
      let maxY = camera.y + camera.height / 2;

      /* Give it a margin when not locked (or the threshold will never be reached) */
      if (!this.hasLock) {
        const margin = camera.width / 15;
        minX += margin;
        minY += margin;
        maxY -= margin;
        maxX -= margin;
      }


      /* Cursor can't get out of the camera borders */
      this.x = Math.max(this.x, minX);
      this.x = Math.min(this.x, maxX);
      this.y = Math.max(this.y, minY);
      this.y = Math.min(this.y, maxY);


      /* Cursor positioned at the camera borders will "push" it */
      switch (this.x) {
        default:
          if (this.bordersTouched.left) {
            camera.movement.x = 0;
            this.bordersTouched.left = false;
          }
          if (this.bordersTouched.right) {
            camera.movement.x = 0;
            this.bordersTouched.right = false;
          }
          break;

        case minX:
          this.bordersTouched.left = true;
          camera.movement.x = -1;
          break;

        case maxX:
          this.bordersTouched.right = true;
          camera.movement.x = 1;
          break;
      }
      switch (this.y) {
        default:
          if (this.bordersTouched.top) {
            camera.movement.y = 0;
            this.bordersTouched.top = false;
          }
          if (this.bordersTouched.bottom) {
            camera.movement.y = 0;
            this.bordersTouched.bottom = false;
          }
          break;

        case minY:
          this.bordersTouched.top = true;
          camera.movement.y = -1;
          break;

        case maxY:
          this.bordersTouched.bottom = true;
          camera.movement.y = 1;
          break;
      }
    }
  };

  onMouseClick = (e: MouseEvent): void => {
    e.preventDefault();
    if (!this.useLock)
      return;

    /* Get pointer lock */
    if (!this.hasLock) {
      const rtsControls = Game.instance.controlSchemes?.rts;
      if (Game.instance.controlSchemes.rts) {
        const { x, y } = rtsControls.getPointerCoords(e);
        this.x = x;
        this.y = y;
      }
      this.context.canvas.requestPointerLock();
    }

    const hoveredElement = this.getHoveredElement();
    if (hoveredElement?.onClick) {
      hoveredElement.onClick();
    }
  }

  getHoveredElement(): Element | undefined {
    return Game.instance.scene.quadtree.colliding({
      x: this.x,
      y: this.y,
      width: 1,
      height: 1
    })
      .map((e: QuadtreeItem) => Game.instance.scene.elements[e.id])
      ?.[0]
  }

  isHoverElem(elem: Element): boolean {
    return elem.scene.quadtree
      ?.filter(el => el.id == elem.id)
      ?.colliding({
        x: this.x,
        y: this.y,
        width: 1,
        height: 1
      })
      ?.length === 1
  }

  create() {
    const { canvas } = this.context;

    /* Re-locking */
    this.eventListenerTarget.addEventListener('click', this.onMouseClick);

    if (this.useLock) {
      this.x = Game.instance.canvas.mainCanvas.width / 2;
      this.y = Game.instance.canvas.mainCanvas.height / 2;
      canvas.requestPointerLock();
    }

    this.eventListenerTarget.addEventListener('mousemove', this.onMouseMove);

    return this;
  }

  get isInCamera(): boolean {
    return true;
  }

  remove() {
    this.eventListenerTarget.removeEventListener('mousemove', this.onMouseMove);
    this.eventListenerTarget.removeEventListener('click', this.onMouseClick);
    delete Game.instance.cursor;
    return Game.instance.scene.elements;
  }
}
